import {inject, Injectable} from '@angular/core';
import {Subscription, timer} from 'rxjs';
import {Router} from '@angular/router';
import {HttpClient} from '@angular/common/http';
import {UrlConstants} from '../../../constants/url.constants';
import {Dialog, DialogRef} from '@angular/cdk/dialog';
import {SessionTimeoutComponent} from '../../../../components/common/session-timeout/session-timeout.component';
import {EventTypeConstants} from '../../../constants/event-type.constants';
import {CommonService} from './common.service';
import {AuthService} from '../../../services/auth.service';

@Injectable({
  providedIn: "root",
})
export class TimeoutService {

  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly dialog = inject(Dialog);
  private readonly authService = inject(AuthService);
  private readonly commonService = inject(CommonService);

  private timeoutValue: number = 0; // Timeout value in milliseconds
  private timeLeft: number = 0; // Time left in milliseconds
  private timerSubscription: Subscription | null = null;
  private warningShown: boolean;

  private warningDialogRef: DialogRef<unknown, SessionTimeoutComponent>;

  setupTimeout(timeout: number) {
    this.timeoutValue = timeout * 1000; // Convert seconds to milliseconds
    this.timeLeft = this.timeoutValue;
    this.warningShown = false;
    this.startTimer();
  }

  // Start the timer
  private startTimer(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe(); // Stop any existing timer
    }

    this.timerSubscription = timer(0, 1000).subscribe(() => {
      this.timeLeft -= 1000;

      if (!this.warningShown && this.timeLeft <= this.timeoutValue / 4) {
        this.warningShown = true;
        this.showWarning(); // Show warning when 1/4 time is left
      }

      if (this.timeLeft <= 0) {
        this.stopTimer();
        this.commonService.logEvent(EventTypeConstants.LOGOUT, "User is logging out due to inactivity", "", undefined, () => {
          this.authService.logout().subscribe({
            next: (res) => {
              this.warningDialogRef?.close();
              this.authService.redirectAfterLogout(res?.redirectUrl);
            },
            error: (err) => {
              console.error('Logout request failed', err);
              this.warningDialogRef?.close();
              this.authService.redirectAfterLogout(undefined);
            }
          });
        });
      }
    });
  }

  restartTimer(): void {
    if (this.timerSubscription) {
      this.keepAlive();
      this.stopTimer();
      this.timeLeft = this.timeoutValue;
    }
    this.startTimer();
  }

  // Stop the timer
  stopTimer(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
    }
  }

  // Show warning when 1/4 time is left
  private showWarning(): void {
    console.warn("Warning: Only 1/4 of the session time is left.");

    this.warningDialogRef = this.dialog.open(SessionTimeoutComponent);

    this.warningDialogRef.closed.subscribe({
      next: (isExtended) => {
        if (isExtended) {
          this.restartTimer();
        }
      },
    });
  }

  public keepAlive() {
    this.http.get(UrlConstants.SESSION_KEEPALIVE_URL).subscribe();
  }
}
